import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Grid, CircularProgress } from "@mui/material";
import {
  DialogComponent,
  SearchBarComponent,
} from "../../../atom";
import DocumentBreadcrumbs from "../../Document/BreadCrumb";
import DocumentGrid from "../../Document/DocumentGrid";
import DocumentRepository from "../../../../core/repository/DocumentRepository";
import { DOCUMENT_STATUS } from "../../../../core/Constant";

const IS_DIRECTORY_FOLDER = 1;
const ROOT_BREADCRUMB = { name: "Documents" };

const isFolder = (doc) => Number(doc?.is_directory) === IS_DIRECTORY_FOLDER;
const normalizeParentId = (parentId) =>
  parentId == null || parentId === 0 ? null : Number(parentId);
const formatTitle = (name = "") => name.replace(/\.[^/.]+$/, "");

const SelectDocumentModal = ({
  open,
  onClose,
  courseID,
  onSelect,
  initialFolderStack,
  selectedDocId,
}) => {
  console.log(initialFolderStack)
  const mainDetailsState = useSelector((state) => state.course.get("mainDetailsState"));
  const courseName = mainDetailsState?.name || "";
  const [documents, setDocuments] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchDocuments = async (parentId = null, search = searchQuery) => {
    if (!courseID) return;
    setLoading(true);
    try {
      const params = parentId != null && parentId !== 0
        ? { course_id: courseID, parent_id: parentId }
        : { course_id: courseID };
      if (search?.trim()) {
        params.search = search.trim();
      }
      const res = await DocumentRepository.getDocumentList(params);
      const docsList = res?.data?.documents;
      if (Array.isArray(docsList)) {
        // Only active documents
        const activeDocs = docsList.filter(
          (doc) => Number(doc.status) === DOCUMENT_STATUS.ACTIVE
        );
        setDocuments(activeDocs);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && courseID) {
      if (initialFolderStack && initialFolderStack.length > 0) {
        const lastFolder = initialFolderStack[initialFolderStack.length - 1];
        const lastFolderId = lastFolder?.id ?? null;
        setCurrentFolderId(lastFolderId);
        setFolderStack(initialFolderStack);
        fetchDocuments(lastFolderId, "");
      } else {
        setCurrentFolderId(null);
        setFolderStack([]);
        fetchDocuments(null, "");
      }
      setSearchQuery("");
    }
  }, [open, courseID, initialFolderStack]);

  const displayList = useMemo(() => {
    let items = documents;
    const hasParentIds = items.some(
      (d) => d.parent_id != null && d.parent_id !== "" && d.parent_id !== 0
    );
    if (hasParentIds) {
      const parentId = currentFolderId ?? null;
      items = items.filter((d) => {
        const docParentId = normalizeParentId(d.parent_id);
        if (parentId != null) {
          return docParentId === normalizeParentId(parentId);
        }
        return (
          docParentId === null || docParentId === normalizeParentId(courseID)
        );
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      items = items.filter((d) => d.name?.toLowerCase().includes(q));
    }
    const folders = items.filter(isFolder);
    const files = items.filter((d) => !isFolder(d));
    return [...folders, ...files];
  }, [documents, currentFolderId, searchQuery, courseID]);

  const openFolder = (doc) => {
    if (loading) return;
    setCurrentFolderId(doc.id);
    setFolderStack((prev) => {
      if (prev.find((folder) => folder.id === doc.id)) return prev;
      return [...prev, { id: doc.id, name: doc.name }];
    });
    fetchDocuments(doc.id);
  };

  const navigateToBreadcrumb = (index) => {
    if (index === 0 || (courseName && index === 1)) {
      setCurrentFolderId(null);
      setFolderStack([]);
      fetchDocuments(null);
      return;
    }
    const folderIndex = index - (courseName ? 2 : 1);
    const newStack = folderStack.slice(0, folderIndex + 1);
    setFolderStack(newStack);
    const folderId = newStack[folderIndex]?.id ?? null;
    setCurrentFolderId(folderId);
    fetchDocuments(folderId);
  };

  const breadcrumbTrail = useMemo(() => {
    const trail = [ROOT_BREADCRUMB];
    if (courseName) {
      trail.push({ id: `course-${courseID}`, name: courseName });
    }
    return [...trail, ...folderStack];
  }, [courseName, courseID, folderStack]);

  const searchInput = (value) => {
    setSearchQuery(value);
    fetchDocuments(currentFolderId, value);
  };

  const handleCardAction = (item) => {
    if (isFolder(item)) {
      openFolder(item);
      return;
    }
    onSelect(item, currentFolderId, folderStack);
  };

  return (
    <DialogComponent
      title="Select Document"
      open={open}
      maxwidth="lg"
      onClose={onClose}
      isShowCloseButton={true}
    >
      <Box p={3} sx={{ position: "relative", minHeight: "450px" }}>
        <Grid container justifyContent="space-between" alignItems="center" mb={2}>
          <Grid item>
            <DocumentBreadcrumbs
              breadcrumbTrail={breadcrumbTrail}
              onBreadcrumbClick={navigateToBreadcrumb}
            />
          </Grid>
          <Grid item>
            <SearchBarComponent
              value={searchQuery}
              onchange={(e) => searchInput(e.target.value)}
            />
          </Grid>
        </Grid>

        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(255,255,255,0.6)",
              zIndex: 9,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress sx={{ color: "#9834F0" }} />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <DocumentGrid
            items={displayList}
            isFolder={isFolder}
            formatTitle={formatTitle}
            handleCardAction={handleCardAction}
            fileBtnText="Select"
            emptyMessage="Currently, there are no active Documents available in this folder."
            selectedDocId={selectedDocId}
          />
        </Box>
      </Box>
    </DialogComponent>
  );
};

export default SelectDocumentModal;
